import { ART_PAL, ball, fade, mix, n, rng, shade, star, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Liftoff scene behind the game zone: the sky over a spaceport — night (dawn in light), the glow of the port rising
 * from below the frame. A ringed planet high on the left, a small moon on the right, soft clouds at the side edges,
 * stars (night) only in the upper side bands. The chart is a window onto this sky; its middle stays calm (a soft pool,
 * nothing but the gradient). No ground, no towers, no lines: nothing would cross the bet panels under the chart.
 * Motifs are marked `data-motif="<x>"` (tests keep them out of the middle 40%). No filters, no text.
 */
export function crashScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sc${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  // the dawn / city glow of the spaceport below the frame: the chart shows the sky, no ground line crosses the panels
  const hz = wide ? 820 : 400;

  const top = P.dark ? mix(P.bg, P.blue, 0.2) : mix(P.sky, '#FFFFFF', 0.8);
  const mid = P.dark ? mix(P.bg, P.purple, 0.1) : mix(P.blue, '#FFFFFF', 0.9);
  const low = P.dark ? mix(P.bg, P.teal, 0.14) : mix(P.orange, '#FFFFFF', 0.8);
  const planet = P.dark ? mix(P.purple, P.bg, 0.25) : tint(P.purple, 0.35);
  const ring = P.dark ? tint(P.gold, 0.1) : P.gold;
  const moon = P.dark ? '#FFF1C2' : '#FFFFFF';
  const cloud = P.dark ? mix(P.bg, P.blue, 0.3) : '#FFFFFF';
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  const defs =
    `<defs>${vgrad(`${p}sk`, top, mid, low)}` +
    ball(`${p}pl`, planet, 0.4, 0.45) +
    ball(`${p}mo`, moon, 0.2, 0.18) +
    fade(`${p}gl`, P.dark ? P.teal : P.orange, P.dark ? 0.16 : 0.26) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.4 : 0.45) +
    `</defs>`;

  /** a soft cloud: overlapping ellipses, flat bottom */
  const clouds = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" fill="${cloud}" fill-opacity="${o(0.5, 0.75)}" transform="translate(${x} ${y}) scale(${s})">` +
    `<ellipse cx="0" cy="0" rx="70" ry="16"/><ellipse cx="-24" cy="-12" rx="30" ry="20"/><ellipse cx="18" cy="-18" rx="36" ry="26"/>` +
    `<ellipse cx="50" cy="-6" rx="24" ry="14"/></g>`;
  /** a ringed planet (low contrast): back half of the ring, the ball, front half of the ring */
  const ringed = (x: number, y: number, r: number) => {
    const arc = (sweep: 0 | 1) =>
      `<path d="M${n(x - r * 1.9)} ${n(y)}A${n(r * 1.9)} ${n(r * 0.5)} 0 0 ${sweep} ${n(x + r * 1.9)} ${n(y)}" fill="none" stroke="${ring}" ` +
      `stroke-width="${n(r * 0.16)}" stroke-opacity="${o(0.55, 0.7)}" transform="rotate(-14 ${x} ${y})"/>`;
    return (
      `<g data-motif="${Math.round(x)}">${arc(1)}<circle cx="${x}" cy="${y}" r="${r}" fill="url(#${p}pl)" fill-opacity="${o(0.7, 0.75)}"/>` +
      `${arc(0)}</g>`
    );
  };
  const r = rng(wide ? 5 : 9);
  let stars = '';
  if (P.dark) {
    // stars at the upper edges only: two bands left and right, none in the middle 40%
    const k = wide ? 26 : 12;
    for (let i = 0; i < k; i++) {
      const left = i % 2 === 0;
      const x = wide ? (left ? 40 + r() * 400 : 1160 + r() * 400) : left ? 10 + r() * 110 : 280 + r() * 110;
      const y = 20 + r() * (wide ? 420 : 200);
      const rr = 0.8 + r() * 1.8;
      stars +=
        i % 5 === 0
          ? `<polygon points="${star(x, y, rr * 2, rr * 0.6, 4)}"/>`
          : `<circle cx="${n(x)}" cy="${n(y)}" r="${n(rr * 0.7)}"/>`;
    }
    stars = `<g fill="${moon}" fill-opacity=".6">${stars}</g>`;
  }

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}sk)"/>` +
    stars +
    // the dawn / city glow along the horizon, then the calm centre
    `<ellipse cx="${W / 2}" cy="${hz}" rx="${n(W * 0.7)}" ry="${n(H * 0.3)}" fill="url(#${p}gl)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.45)}" rx="${n(W * 0.34)}" ry="${n(H * 0.36)}" fill="url(#${p}calm)"/>`;

  if (wide) {
    art +=
      ringed(350, 270, 28) +
      `<circle data-motif="1190" cx="1190" cy="250" r="13" fill="url(#${p}mo)" fill-opacity="${o(0.6, 0.85)}"/>` +
      clouds(380, 520, 1) +
      clouds(1210, 590, 0.7) +
      clouds(1480, 470, 1.1);
  } else {
    art += ringed(50, 205, 11) + clouds(110, 130, 0.45) + clouds(300, 280, 0.45);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Liftoff: a row of runway lights on a thin line (repeat-x tile, 24 × 8). */
export function crashAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const line = P.dark ? mix(P.bg, P.sky, 0.45) : tint(P.blue, 0.4);
  const lamp = P.dark ? P.gold : shade(P.orange, 0.05);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 8" width="24" height="8">` +
    `<path d="M0 6.5H24" stroke="${line}" stroke-opacity=".8"/>` +
    `<circle cx="12" cy="4" r="1.6" fill="${lamp}"/><circle cx="12" cy="4" r="3" fill="${lamp}" fill-opacity=".25"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'crash', scene: crashScene, accent: crashAccent });
